import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import StatusBadge from './StatusBadge';
import ContactActions from './ContactActions';
import NotesEditor from './NotesEditor';
import SearchFilterBar from './SearchFilterBar';
import SendPaymentLinkButton from './SendPaymentLinkButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const STUDENT_STATUS_OPTIONS = [
  { value: 'new', label: 'New Lead' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'matched', label: 'Matched with Tutor' },
  { value: 'trial_booked', label: 'Trial Booked' },
  { value: 'active', label: 'Active Student' },
  { value: 'closed', label: 'Closed / Lost' }
];

/**
 * Student Management Panel Component
 * 
 * Manages student enquiries with status updates, tutor assignment, and notes
 */
const StudentManagementPanel = ({ students, tutors, onRefresh, processing }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const updateStatus = async (studentId, newStatus, studentName) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v4/admin/students/${studentId}/status?status=${newStatus}`,
        { method: 'PATCH', credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Failed to update status');
      toast.success(`${studentName} status updated to: ${newStatus.replace(/_/g, ' ')}`);
      onRefresh();
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Failed to update status');
    }
  };
  
  const assignTutor = async (studentId, tutorId, studentName) => {
    try {
      const formData = new FormData();
      formData.append('tutor_id', tutorId);
      
      const response = await fetch(
        `${BACKEND_URL}/api/v4/admin/students/${studentId}/assign`,
        { method: 'POST', credentials: 'include', body: formData }
      );
      
      if (!response.ok) throw new Error('Failed to assign tutor');
      toast.success(`Tutor assigned to ${studentName}`);
      onRefresh();
    } catch (error) {
      console.error('Assignment error:', error);
      toast.error('Failed to assign tutor');
    }
  };
  
  const saveNotes = async (studentId, notes, studentName) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v4/admin/students/${studentId}/notes?admin_notes=${encodeURIComponent(notes)}`,
        { method: 'PATCH', credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Failed to save notes');
      toast.success(`Notes saved for ${studentName}`);
      onRefresh();
    } catch (error) {
      console.error('Notes save error:', error);
      toast.error('Failed to save notes');
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  
  // Filter students
  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      student.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.parent_name && student.parent_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || student.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  
  // Get approved tutors for assignment
  const approvedTutors = tutors.filter(t => t.status === 'approved');
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Student Enquiries ({filteredStudents.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <SearchFilterBar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          statusOptions={STUDENT_STATUS_OPTIONS}
          placeholder="Search students by name or email..."
        />
        
        <div className="space-y-4">
          {filteredStudents.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No student enquiries found</p>
          ) : (
            filteredStudents.map(student => (
              <Card key={student.id} className="border">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {student.student_name}
                        </h3>
                        <div className="flex flex-col gap-1 mt-2 text-sm text-gray-600">
                          {student.parent_name && (
                            <span>Parent: {student.parent_name}</span>
                          )}
                          <span>{student.email}</span>
                          <span>{student.phone}</span>
                        </div>
                      </div>
                      <StatusBadge status={student.status} type="student" />
                    </div>
                    
                    {/* Program Details */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Program:</p>
                        <p className="font-medium">{student.program_type}</p>
                      </div>
                      {student.year_level && (
                        <div>
                          <p className="text-gray-500">Year Level:</p>
                          <p className="font-medium">{student.year_level}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-gray-500">Subject:</p>
                        <p className="font-medium">{student.subject}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Format:</p>
                        <p className="font-medium">{student.preferred_format}</p>
                      </div>
                    </div>
                    
                    {/* Timestamps */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Submitted:</p>
                        <p className="font-medium">{formatDate(student.submitted_at)}</p>
                      </div>
                      {student.contacted_at && (
                        <div>
                          <p className="text-gray-500">Last Contacted:</p>
                          <p className="font-medium">{formatDate(student.contacted_at)}</p>
                        </div>
                      )}
                      {student.assigned_at && (
                        <div>
                          <p className="text-gray-500">Assigned:</p>
                          <p className="font-medium">{formatDate(student.assigned_at)}</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Preferred Tutor (Student's Choice) */}
                    {student.tutor_preference === 'specific' && student.requested_tutor_name && (
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
                        <p className="text-sm font-semibold text-blue-900">Preferred Tutor:</p>
                        <p className="text-sm text-gray-900 mt-1">{student.requested_tutor_name}</p>
                        <p className="text-xs text-blue-700 mt-1">Selected by student</p>
                      </div>
                    )}
                    {student.tutor_preference === 'match' && (
                      <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-3">
                        <p className="text-sm font-semibold text-gray-700">Preferred Tutor:</p>
                        <p className="text-sm text-gray-600 mt-1">No Preference</p>
                        <p className="text-xs text-gray-500 mt-1">Student requested best match</p>
                      </div>
                    )}
                    
                    {/* Assigned Tutor (Admin Control) */}
                    {student.assigned_tutor_name && (
                      <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3">
                        <p className="text-sm font-semibold text-green-900">Assigned Tutor:</p>
                        <p className="text-sm text-gray-900 mt-1">{student.assigned_tutor_name}</p>
                        <p className="text-xs text-green-700 mt-1">Allocated by Winhouse Tutors</p>
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex flex-wrap gap-3 pt-4 border-t">
                      <Select
                        value={student.status}
                        onValueChange={(value) => updateStatus(student.id, value, student.student_name)}
                        disabled={processing}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STUDENT_STATUS_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Select
                        value={student.assigned_tutor_id || ''}
                        onValueChange={(value) => assignTutor(student.id, value, student.student_name)}
                        disabled={processing || approvedTutors.length === 0}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Assign Tutor" />
                        </SelectTrigger>
                        <SelectContent>
                          {approvedTutors.map(tutor => (
                            <SelectItem key={tutor.id} value={tutor.id}>
                              {tutor.full_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <ContactActions
                        email={student.email}
                        phone={student.phone}
                        name={student.student_name}
                      />

                      <SendPaymentLinkButton
                        studentName={student.student_name}
                        studentEmail={student.email}
                        studentPhone={student.phone}
                        testIdSuffix={student.id}
                      />
                    </div>
                    
                    {/* Student Notes */}
                    {student.notes && (
                      <div className="pt-4 border-t">
                        <p className="text-sm font-medium text-gray-700 mb-1">Student Notes:</p>
                        <p className="text-sm text-gray-600">{student.notes}</p>
                      </div>
                    )}
                    
                    {/* Admin Notes */}
                    <div className="pt-4 border-t">
                      <p className="text-sm font-medium text-gray-700 mb-2">Admin Notes:</p>
                      <NotesEditor
                        initialNotes={student.admin_notes}
                        onSave={(notes) => saveNotes(student.id, notes, student.student_name)}
                        disabled={processing}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default StudentManagementPanel;